/**
 * Lot Number Helpers - Montreal Kosher
 * Utilities for generating and converting lot numbers in Julian date format
 */

/**
 * Generate Julian date lot number (DDDX format)
 * DDD = day of year (001-365)
 * X = last digit of year
 * Example: 0566 = day 56 of 2026
 * @returns {string} 4-character lot number
 */
export const generateLotNumber = () => {
  const now = new Date();
  const lastDigit = now.getFullYear().toString().slice(-1);
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  const dayStr = dayOfYear.toString().padStart(3, '0');
  return `${dayStr}${lastDigit}`;
};

/**
 * Convert a date string to Julian format (DDDX)
 * @param {string} dateStr - Date string in ISO format (YYYY-MM-DD)
 * @returns {string} 4-character lot number
 */
export const dateToJulian = (dateStr) => {
  const date = new Date(dateStr);
  const lastDigit = date.getFullYear().toString().slice(-1);
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  return `${dayOfYear.toString().padStart(3, '0')}${lastDigit}`;
};

/**
 * Convert Julian lot number back to date
 * @param {string} julian - 4-character lot number (DDDX)
 * @param {number} baseYear - The century base year (e.g., 2020 for 202X)
 * @returns {Date|null} Date object or null if invalid
 */
export const julianToDate = (julian, baseYear = 2020) => {
  if (!julian || julian.length !== 4) return null;

  const dayOfYear = parseInt(julian.substring(0, 3), 10);
  const yearDigit = parseInt(julian.substring(3), 10);
  const year = baseYear + yearDigit;

  if (isNaN(dayOfYear) || isNaN(yearDigit) || dayOfYear < 1 || dayOfYear > 366) {
    return null;
  }

  const date = new Date(year, 0, dayOfYear);
  return date;
};

/**
 * Validate lot number format
 * @param {string} lotNumber - The lot number to validate
 * @returns {boolean} True if valid DDDX format
 */
export const isValidLotNumber = (lotNumber) => {
  if (!lotNumber || lotNumber.length !== 4) return false;

  const dayPart = lotNumber.substring(0, 3);
  const yearPart = lotNumber.substring(3);

  const dayNum = parseInt(dayPart, 10);
  const yearNum = parseInt(yearPart, 10);

  return !isNaN(dayNum) && !isNaN(yearNum) && dayNum >= 1 && dayNum <= 366 && yearNum >= 0 && yearNum <= 9;
};

export default {
  generateLotNumber,
  dateToJulian,
  julianToDate,
  isValidLotNumber
};
